<template>
  <div>
    <h1 class="page-header">ZKBio CVSecurity Settings</h1>
    <ts-card>
      <ts-card-wrapper>
        <div class="row mb-3">
          <div class="col-lg-9">
            <p class="ts-text-gray-500">
              <b><i><span class="text-danger">* </span>Note:</i></b>
              Only user at head office can modify these settings.
            </p>
          </div>
        </div>
        <hr style="height: 1px" />
        <br />
        <form class="form-horizontal">
          <div class="row">
            <div class="col-md-6">
              <div class="row form-group">
                <label class="col-md-4 col-form-label required">Protocol</label>
                <div class="col-md-8">
                  <select v-model="model.server_protocol" class="form-control">
                    <option value="http">HTTP</option>
                    <option value="https">HTTPS</option>
                  </select>
                </div>
              </div>

              <div class="row form-group">
                <label class="col-md-4 col-form-label required">Server IP Address</label>
                <div class="col-md-8">
                  <input
                    v-model="model.server_ip"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('server_ip') }"
                    placeholder="e.g. 192.168.1.100"
                  />
                  <div class="invalid-feedback" v-if="errors.has('server_ip')">
                    {{ errors.first('server_ip') }}
                  </div>
                </div>
              </div>

              <div class="row form-group">
                <label class="col-md-4 col-form-label required">Port</label>
                <div class="col-md-8">
                  <input
                    v-model.number="model.server_port"
                    type="number"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('server_port') }"
                    placeholder="e.g. 8098"
                  />
                  <div class="invalid-feedback" v-if="errors.has('server_port')">
                    {{ errors.first('server_port') }}
                  </div>
                </div>
              </div>

              <div class="row form-group">
                <label class="col-md-4 col-form-label required">Access Token</label>
                <div class="col-md-8">
                  <input
                    v-model="model.access_token"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('access_token') }"
                    placeholder="Enter ZKBio API access token"
                  />
                  <div class="invalid-feedback" v-if="errors.has('access_token')">
                    {{ errors.first('access_token') }}
                  </div>
                </div>
              </div>

              <div class="row form-group">
                <label class="col-md-4 col-form-label">Parent Access Level ID</label>
                <div class="col-md-8">
                  <a-select
                    v-if="accLevels.length > 0"
                    v-model="model.parent_acc_level_id"
                    style="width: 100%"
                    placeholder="Select access level (scan-only)"
                    allow-clear
                    show-search
                    option-filter-prop="children"
                  >
                    <a-select-option
                      v-for="lvl in accLevels"
                      :key="String(lvl.id)"
                      :value="String(lvl.id)"
                    >
                      {{ lvl.name }}
                    </a-select-option>
                  </a-select>
                  <input
                    v-else
                    v-model="model.parent_acc_level_id"
                    type="text"
                    class="form-control"
                    placeholder="ZKBio accLevelId for scan-only (no door open)"
                  />
                  <small class="text-muted">
                    Assign parents to a restricted ZKBio access level so their card scan is recorded but no door is opened. Leave blank to use ZKBio default.
                  </small>
                </div>
              </div>

              <div class="row form-group">
                <label class="col-md-4 col-form-label">Student Access Level ID</label>
                <div class="col-md-8">
                  <a-select
                    v-if="accLevels.length > 0"
                    v-model="model.student_acc_level_id"
                    style="width: 100%"
                    placeholder="Select access level for students"
                    allow-clear
                    show-search
                    option-filter-prop="children"
                  >
                    <a-select-option
                      v-for="lvl in accLevels"
                      :key="String(lvl.id)"
                      :value="String(lvl.id)"
                    >
                      {{ lvl.name }}
                    </a-select-option>
                  </a-select>
                  <input
                    v-else
                    v-model="model.student_acc_level_id"
                    type="text"
                    class="form-control"
                    placeholder="ZKBio accLevelId for students (door access)"
                  />
                  <small class="text-muted">Access level assigned to students when their card is activated.</small>
                </div>
              </div>

              <div class="row form-group">
                <label class="col-md-4 col-form-label">Staff Access Level ID</label>
                <div class="col-md-8">
                  <a-select
                    v-if="accLevels.length > 0"
                    v-model="model.staff_acc_level_id"
                    style="width: 100%"
                    placeholder="Select access level for staff"
                    allow-clear
                    show-search
                    option-filter-prop="children"
                  >
                    <a-select-option
                      v-for="lvl in accLevels"
                      :key="String(lvl.id)"
                      :value="String(lvl.id)"
                    >
                      {{ lvl.name }}
                    </a-select-option>
                  </a-select>
                  <input
                    v-else
                    v-model="model.staff_acc_level_id"
                    type="text"
                    class="form-control"
                    placeholder="ZKBio accLevelId for staff (door access)"
                  />
                  <small class="text-muted">Access level assigned to staff when their card is activated.</small>
                </div>
              </div>

              <div class="row form-group">
                <label class="col-md-4 col-form-label">Enable Auto Sync</label>
                <div class="col-md-8" style="margin-top: 6px">
                  <a-switch v-model="model.enabled" />
                </div>
              </div>

              <div class="row form-group" v-if="resource && resource.last_sync_at">
                <label class="col-md-4 col-form-label">Last Synced At</label>
                <div class="col-md-8 col-form-label ts-text-gray-500">
                  {{ resource.last_sync_at }}
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-12 text-right ts-mt-12">
            <ts-button class="btn-primary" :waiting="waiting" @click="save">
              Save
            </ts-button>
          </div>
        </form>

        <hr style="height: 1px" />

        <h5 class="mt-3 mb-3">ZKBio Gate Device Mapping</h5>
        <p class="ts-text-gray-500" style="font-size:13px">
          Map each ZKBio gate device serial number to its corresponding school attendance device so branch can be resolved automatically on scan.
        </p>

        <div class="row mb-3">
          <div class="col-md-4">
            <a-select
              v-if="zkbioDevices.length > 0"
              v-model="newMap.zkbio_device_sn"
              style="width: 100%"
              placeholder="Select ZKBio gate device"
              allow-clear
              show-search
              option-filter-prop="children"
            >
              <a-select-option
                v-for="d in zkbioDevices"
                :key="d.sn"
                :value="d.sn"
              >
                {{ d.name }} ({{ d.sn }})
              </a-select-option>
            </a-select>
            <input
              v-else
              v-model="newMap.zkbio_device_sn"
              type="text"
              class="form-control"
              placeholder="ZKBio Device SN (from API deviceSn)"
            />
          </div>
          <div class="col-md-4">
            <a-select
              v-model="newMap.attendance_device_id"
              style="width: 100%"
              placeholder="Select attendance device"
              allow-clear
              show-search
              option-filter-prop="children"
            >
              <a-select-option v-for="d in devices" :key="d.id" :value="d.id">
                {{ d.device_description || d.device_ip }}
              </a-select-option>
            </a-select>
          </div>
          <div class="col-md-4">
            <ts-button class="btn-success" :waiting="addingMap" @click="addDeviceMap">
              Add Mapping
            </ts-button>
          </div>
        </div>

        <table class="table table-bordered table-sm" v-if="deviceMaps.length">
          <thead class="thead-light">
            <tr>
              <th>ZKBio Device SN</th>
              <th>Attendance Device</th>
              <th>Branch</th>
              <th style="width:80px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in deviceMaps" :key="m.id">
              <td>{{ m.zkbio_device_sn }}</td>
              <td>{{ m.attendance_device && (m.attendance_device.device_description || m.attendance_device.device_ip) }}</td>
              <td>{{ m.attendance_device && m.attendance_device.branch && m.attendance_device.branch.branch_name_en }}</td>
              <td class="text-center">
                <a-popconfirm title="Remove this mapping?" @confirm="removeDeviceMap(m.id)">
                  <a class="text-danger"><i class="fas fa-trash"></i></a>
                </a-popconfirm>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="ts-text-gray-500">No device mappings configured.</p>

      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapState } from 'vuex'
import { getDevices } from '@/api/zkbio-setting'
import httpClient from '@/http-client'

export default {
  name: 'zkbio-setting',
  data() {
    return {
      waiting: false,
      errors: new Errors(),
      devices: [],
      deviceMaps: [],
      accLevels: [],
      zkbioDevices: [],
      addingMap: false,
      newMap: {
        zkbio_device_sn: '',
        attendance_device_id: null
      },
      model: {
        server_ip: '',
        server_protocol: 'http',
        server_port: 8098,
        access_token: '',
        parent_acc_level_id: '',
        student_acc_level_id: '',
        staff_acc_level_id: '',
        enabled: false
      }
    }
  },
  created() {
    this.fetchDevices()
    this.fetchDeviceMaps()
    this.fetchData()
    this.fetchAccLevels()
    this.fetchZkbioDevices()
  },
  computed: {
    ...mapState('zkbioSetting', ['resource'])
  },
  methods: {
    fetchAccLevels() {
      httpClient.get('/api/zkbio-settings/acc-levels')
        .then(r => { this.accLevels = Array.isArray(r.data) ? r.data : [] })
        .catch(() => { this.accLevels = [] })
    },
    fetchZkbioDevices() {
      httpClient.get('/api/zkbio-settings/zkbio-devices')
        .then(r => { this.zkbioDevices = Array.isArray(r.data) ? r.data : [] })
        .catch(() => { this.zkbioDevices = [] })
    },
    fetchDevices() {
      getDevices()
        .then(data => { this.devices = data })
        .catch(() => {})
    },
    fetchDeviceMaps() {
      httpClient.get('/api/zkbio-device-maps')
        .then(r => { this.deviceMaps = r.data })
        .catch(() => {})
    },
    addDeviceMap() {
      if (!this.newMap.zkbio_device_sn || !this.newMap.attendance_device_id) {
        this.$toasted.error('Device SN and attendance device are required.')
        return
      }
      this.addingMap = true
      httpClient.post('/api/zkbio-device-maps', this.newMap)
        .then(() => {
          this.newMap = { zkbio_device_sn: '', attendance_device_id: null }
          this.fetchDeviceMaps()
          this.$toasted.success('Device mapping saved.')
        })
        .catch(e => { this.$toasted.error((e.response && e.response.data && e.response.data.message) || 'Failed to save.') })
        .finally(() => { this.addingMap = false })
    },
    removeDeviceMap(id) {
      httpClient.delete('/api/zkbio-device-maps/' + id)
        .then(() => {
          this.fetchDeviceMaps()
          this.$toasted.success('Mapping removed.')
        })
        .catch(() => { this.$toasted.error('Failed to remove.') })
    },
    fetchData() {
      this.$store
        .dispatch('zkbioSetting/fetch')
        .then(response => {
          if (response) {
            this.model = {
              server_ip: response.server_ip,
              server_port: response.server_port || 8098,
              access_token: response.access_token,
              server_protocol: response.server_protocol || 'http',
              parent_acc_level_id: response.parent_acc_level_id || '',
              student_acc_level_id: response.student_acc_level_id || '',
              staff_acc_level_id: response.staff_acc_level_id || '',
              enabled: response.enabled
            }
          }
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    save() {
      this.errors = new Errors()
      this.waiting = true
      this.$store
        .dispatch('zkbioSetting/store', this.model)
        .then(response => {
          this.waiting = false
          this.fetchData()
          this.$toasted.success(response.message)
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.waiting = false
          this.$toasted.error(error.message)
        })
    }
  }
}
</script>
